<template>
  <div class="visa-page ahrs-home-bg">
    <!-- Editorial Hero -->
    <section class="ahrs-page-hero hero-photo-visa">
      <div class="ahrs-page-hero-wrap">
        <div class="ahrs-page-eyebrow" data-aos="fade-up">
          <span class="dot"></span>Travel &amp; Entry
        </div>
        <h1 class="ahrs-page-title" data-aos="fade-up" data-aos-delay="100">
          Visa &amp; entry
          <span class="ahrs-page-italic">requirements for Tanzania.</span>
        </h1>
        <p class="ahrs-page-lede" data-aos="fade-up" data-aos-delay="200">
          Everything delegates need to enter Tanzania for the 2<sup>nd</sup>
          Africa Human Resource Summit &mdash; Mount Meru Hotel, Arusha,
          19&ndash;22 August 2026.
        </p>
        <div class="ahrs-page-meta" data-aos="fade-up" data-aos-delay="300">
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <rect x="3" y="4.5" width="18" height="17" rx="2" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" />
            </svg>
            19&ndash;22 August 2026
          </div>
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
              <circle cx="12" cy="10" r="2.6" />
            </svg>
            Mount Meru Hotel, Arusha
          </div>
        </div>
        <div class="ahrs-page-actions" data-aos="fade-up" data-aos-delay="400">
          <a
            href="https://visa.immigration.go.tz/"
            target="_blank"
            rel="noopener noreferrer"
            class="hero-apply-btn"
          >
            <i class="fas fa-arrow-up-right-from-square"></i>
            Apply for e-Visa
          </a>
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="visa-content">
      <div class="container-narrow">
        <div class="visa-grid">

        <!-- Eligibility checker -->
        <div class="visa-card checker-card span-2" data-aos="fade-up">
          <div class="visa-card-icon"><i class="fas fa-passport"></i></div>
          <h2 class="visa-card-title">Check Visa on Arrival eligibility</h2>
          <p class="visa-card-text">
            Enter the country that issued your passport to see whether your
            nationality can obtain a visa on arrival in Tanzania for the Summit.
          </p>

          <div class="elig-box" ref="eligBox">
            <label class="elig-field">
              <i class="fas fa-magnifying-glass"></i>
              <input
                v-model="country"
                type="text"
                class="elig-input"
                placeholder="Start typing your country…"
                autocomplete="off"
                @input="open = true"
                @focus="open = true"
                @keydown.esc="open = false"
              />
              <button
                v-if="country"
                class="elig-clear"
                type="button"
                aria-label="Clear"
                @click="clearCountry"
              >
                <i class="fas fa-xmark"></i>
              </button>
            </label>

            <transition name="dd-fade">
              <ul v-if="open && suggestions.length" class="elig-dropdown">
                <li
                  v-for="c in suggestions"
                  :key="c"
                  class="elig-option"
                  @mousedown.prevent="selectCountry(c)"
                >
                  <i class="fas fa-location-dot"></i>
                  <span v-html="highlight(c)"></span>
                </li>
              </ul>
            </transition>

            <p v-if="country.trim().length === 1" class="elig-hint">
              Keep typing to see matches…
            </p>
          </div>

          <transition name="elig-fade">
            <div v-if="result" :class="['elig-result', `tone-${result.status}`]">
              <div class="elig-result-head">
                <i :class="result.icon"></i>
                <div>
                  <p class="elig-country">{{ country }}</p>
                  <p class="elig-verdict">{{ result.title }}</p>
                </div>
              </div>
              <p class="elig-detail">{{ result.detail }}</p>
              <p v-if="result.note" class="elig-note">
                <i class="fas fa-circle-info"></i> {{ result.note }}
              </p>
            </div>
          </transition>

          <p class="visa-card-note">
            Eligibility lists follow the Tanzania Immigration Services Visa
            Guidelines and may change without notice &mdash; always confirm on
            the official portal before you travel.
          </p>
        </div>

        <!-- Apply online -->
        <div class="visa-card highlight-card" data-aos="fade-up">
          <div class="visa-card-icon"><i class="fas fa-globe-africa"></i></div>
          <h2 class="visa-card-title">Apply online (e-Visa)</h2>
          <p class="visa-card-text">
            There are two ways to obtain a Tanzania visa: applying online for an
            e-Visa, or getting a visa on arrival at an official entry point. The
            Immigration Services <strong>recommend the online platform</strong>.
            Apply early &mdash; processing takes up to <strong>10 working
            days</strong> (longer for referral cases).
          </p>
          <a
            href="https://visa.immigration.go.tz/"
            target="_blank"
            rel="noopener noreferrer"
            class="visa-btn"
          >
            <i class="fas fa-arrow-up-right-from-square"></i>
            Apply on the official portal
          </a>
          <p class="visa-card-note">
            visa.immigration.go.tz &mdash; Tanzania Immigration Services Department
          </p>
        </div>

        <!-- Invitation letter -->
        <div class="visa-card highlight-card" data-aos="fade-up">
          <div class="visa-card-icon"><i class="fas fa-envelope-open-text"></i></div>
          <h2 class="visa-card-title">Invitation &amp; confirmation letter</h2>
          <p class="visa-card-text">
            Registered delegates can request an official invitation /
            confirmation letter from the AHRS Secretariat to support a visa
            application.
          </p>
          <a href="mailto:register@africahrsummit.org" class="visa-link">
            <i class="fas fa-envelope"></i> register@africahrsummit.org
          </a>
        </div>

        <!-- Documents & photo -->
        <div class="visa-card" data-aos="fade-up">
          <h2 class="visa-card-title">What you&rsquo;ll need to apply</h2>
          <ul class="visa-list">
            <li>A copy of your <strong>passport bio-data page</strong> (valid 6+ months, with a blank visa page).</li>
            <li>A <strong>return / onward ticket</strong> &mdash; you must also show it on arrival.</li>
            <li>The completed online application &amp; declaration form.</li>
            <li>Visa fee, paid online by <strong>Visa or Mastercard</strong>.</li>
            <li>
              A recent <strong>passport photo</strong>: colour, plain background,
              full face, <strong>JPG/JPEG, max 500&nbsp;KB</strong>.
            </li>
          </ul>
        </div>

        <!-- Checklist -->
        <div class="visa-card" data-aos="fade-up">
          <h2 class="visa-card-title">Before you travel &mdash; checklist</h2>
          <ul class="visa-check">
            <li><i class="fas fa-check"></i> Passport valid for at least <strong>6 months</strong> beyond your travel dates, with a blank visa page.</li>
            <li><i class="fas fa-check"></i> Approved visa (e-Visa Grant Notice printout) or funds for visa on arrival.</li>
            <li><i class="fas fa-check"></i> <strong>Return / onward ticket</strong> &mdash; required at the entry point.</li>
            <li><i class="fas fa-check"></i> <strong>Yellow fever certificate</strong> if arriving from or transiting an endemic country.</li>
            <li><i class="fas fa-check"></i> Summit registration confirmation and invitation letter.</li>
            <li><i class="fas fa-check"></i> Each traveller &mdash; including minors &mdash; needs their own application.</li>
          </ul>
        </div>

        <!-- Which visa -->
        <div class="visa-card span-2" data-aos="fade-up">
          <h2 class="visa-card-title">Types of visa &amp; fees</h2>
          <p class="visa-card-text">
            Tanzania issues six visa types. Most Summit delegates need the
            <strong>Ordinary Visa</strong>, which covers attending meetings,
            workshops and exhibitions. <strong>US passport holders must apply
            for a Multiple-Entry Visa</strong>, even for a single visit.
          </p>
          <div class="visa-table-wrap">
            <table class="visa-table">
              <thead>
                <tr><th>Visa type</th><th>Fee</th><th>Validity</th></tr>
              </thead>
              <tbody>
                <tr class="row-flag">
                  <td><strong>Ordinary (Single Entry)</strong><span>Tourism, meetings, workshops, exhibitions</span></td>
                  <td>USD&nbsp;50</td>
                  <td>Up to 90 days</td>
                </tr>
                <tr>
                  <td><strong>Multiple Entry</strong><span>US nationals, non-resident directors, family visits</span></td>
                  <td>USD&nbsp;100</td>
                  <td>Up to 1 year (90 days/stay)</td>
                </tr>
                <tr>
                  <td><strong>Business Visa</strong><span>Short-term professional assignments</span></td>
                  <td>USD&nbsp;250</td>
                  <td>Up to 90 days</td>
                </tr>
                <tr>
                  <td><strong>Transit Visa</strong><span>Transiting to another country</span></td>
                  <td>USD&nbsp;30</td>
                  <td>Up to 7 days</td>
                </tr>
                <tr>
                  <td><strong>Student Visa</strong><span>Academic / research purposes</span></td>
                  <td>USD&nbsp;50&ndash;550</td>
                  <td>By programme length</td>
                </tr>
                <tr>
                  <td><strong>Gratis Visa</strong><span>Diplomatic / official passports</span></td>
                  <td>Free</td>
                  <td>As authorised</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Travel cross-link -->
        <div class="visa-cta span-2" data-aos="zoom-in">
          <h2 class="cta-title">Sorted your visa? Plan your journey.</h2>
          <p class="cta-subtitle">
            See airport, transfer, and getting-to-Arusha details on our travel page.
          </p>
          <router-link to="/travel" class="cta-btn">
            <i class="fas fa-plane"></i> Travel &amp; Transfers
          </router-link>
        </div>

        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "VisaView",
  data() {
    return {
      country: "",
      open: false,
      // EAC + SADC (except Comoros) — no visa required
      visaFree: [
        "Kenya", "Uganda", "Rwanda", "Burundi", "South Sudan",
        "Democratic Republic of the Congo", "Angola", "Botswana", "Eswatini",
        "Lesotho", "Madagascar", "Malawi", "Mauritius", "Mozambique",
        "Namibia", "Seychelles", "South Africa", "Zambia", "Zimbabwe",
      ],
      // Eligible for e-Visa AND visa on arrival
      onArrival: [
        "Albania", "Algeria", "Andorra", "Argentina", "Armenia", "Australia",
        "Austria", "Bahrain", "Belarus", "Belgium", "Benin", "Bhutan",
        "Bolivia", "Bosnia and Herzegovina", "Brazil", "Bulgaria", "Cabo Verde",
        "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chile",
        "China", "Colombia", "Comoros", "Congo Republic", "Costa Rica",
        "Cote d'Ivoire", "Croatia", "Cuba", "Czech Republic", "Denmark",
        "Djibouti", "Ecuador", "Egypt", "Estonia", "Ethiopia", "Finland",
        "France", "Gabon", "Georgia", "Germany", "Ghana", "Greece", "Hungary",
        "Iceland", "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan",
        "Jordan", "Kuwait", "Kosovo", "Liberia", "Libya", "Lithuania",
        "Luxembourg", "Maldives", "Mexico", "Monaco", "Mongolia", "Montenegro",
        "Morocco", "Myanmar (Burma)", "Nepal", "Netherlands", "New Zealand",
        "North Korea", "North Macedonia", "Norway", "Oman", "Paraguay", "Peru",
        "Philippines", "Poland", "Portugal", "Qatar", "Russia", "Saudi Arabia",
        "Senegal", "Serbia", "Slovakia", "Slovenia", "South Korea", "Spain",
        "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Thailand", "Togo",
        "Tunisia", "Turkey", "Ukraine", "United Arab Emirates", "United Kingdom",
        "United States of America", "Uruguay", "Venezuela", "Vietnam",
        "American Samoa", "Azerbaijan", "Curacao", "East Timor", "El Salvador",
        "Equatorial Guinea", "Faroe Islands", "Fiji", "French Guiana",
        "French Polynesia", "Greenland", "Guadeloupe", "Guam", "Guatemala",
        "Haiti", "Holy See", "Honduras", "Kazakhstan", "Latvia", "Liechtenstein",
        "Marshall Islands", "Mayotte", "Micronesia", "Moldova", "New Caledonia",
        "Palau", "Northern Mariana Islands", "Panama", "Laos", "Puerto Rico",
        "San Marino", "Sao Tome and Principe", "Saint Martin", "Suriname",
        "Timor-Leste", "Virgin Islands",
      ],
      // Referral category — cannot get visa on arrival; apply online in advance
      referral: [
        "Afghanistan", "Bangladesh", "Burkina Faso", "Chad", "Eritrea",
        "Guinea", "Guinea-Bissau", "Iran", "Iraq", "Kyrgyzstan", "Lebanon",
        "Mali", "Mauritania", "Niger", "Nigeria", "Pakistan", "Palestine",
        "Somalia", "Syria", "Sierra Leone", "Tajikistan", "Turkmenistan",
        "Uzbekistan", "Yemen",
      ],
    };
  },
  computed: {
    allCountries() {
      // Dropdown lists the countries eligible for Visa on Arrival
      return [...new Set(this.onArrival)].sort((a, b) => a.localeCompare(b));
    },
    suggestions() {
      const q = this.country.trim().toLowerCase();
      if (q.length < 2) return [];
      const starts = [];
      const contains = [];
      for (const c of this.allCountries) {
        const lc = c.toLowerCase();
        if (lc === q) continue; // already an exact selection
        if (lc.startsWith(q)) starts.push(c);
        else if (lc.includes(q)) contains.push(c);
      }
      return [...starts, ...contains].slice(0, 8);
    },
    result() {
      const q = this.country.trim().toLowerCase();
      if (!q) return null;
      const inList = (list) => list.some((c) => c.toLowerCase() === q);

      if (inList(this.referral)) {
        return {
          status: "referral",
          icon: "fas fa-triangle-exclamation",
          title: "Referral category — apply online in advance",
          detail:
            "Your nationality cannot obtain a visa on arrival. You must apply online and wait for approval before travelling — submit at least two months before the Summit and upload an invitation letter addressed to the Commissioner General of Immigration.",
          note:
            "Don't book non-refundable flights or hotels until your visa is approved.",
        };
      }
      if (inList(this.visaFree)) {
        return {
          status: "free",
          icon: "fas fa-circle-check",
          title: "No visa required",
          detail:
            "As an East African Community / SADC national you can enter Tanzania without a visa. Bring a passport valid for at least 6 months and your return ticket.",
          note: "",
        };
      }
      if (inList(this.onArrival)) {
        const usa = q === "united states of america";
        return {
          status: "arrival",
          icon: "fas fa-circle-check",
          title: "Eligible for e-Visa or Visa on Arrival",
          detail:
            "You can apply online for an Ordinary Visa (USD 50, valid up to 90 days) — recommended — or obtain a visa on arrival at Kilimanjaro International Airport. Carry the fee in US dollars cash if paying on arrival.",
          note: usa
            ? "As a US passport holder you must apply for a Multiple-Entry Visa (USD 100), even for a single visit / tourism."
            : "",
        };
      }
      return {
        status: "online",
        icon: "fas fa-circle-info",
        title: "Apply online for an e-Visa",
        detail:
          "We recommend applying online for an Ordinary Visa (USD 50) ahead of travel. Allow up to 10 working days for processing. If you're unsure of your category, contact Tanzania Immigration at info@immigration.go.tz.",
        note: "",
      };
    },
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    selectCountry(c) {
      this.country = c;
      this.open = false;
    },
    clearCountry() {
      this.country = "";
      this.open = false;
    },
    onClickOutside(e) {
      if (this.$refs.eligBox && !this.$refs.eligBox.contains(e.target)) {
        this.open = false;
      }
    },
    highlight(c) {
      const q = this.country.trim().toLowerCase();
      const i = c.toLowerCase().indexOf(q);
      if (!q || i === -1) return c;
      return (
        c.slice(0, i) +
        '<mark class="elig-hl">' +
        c.slice(i, i + q.length) +
        "</mark>" +
        c.slice(i + q.length)
      );
    },
  },
};
</script>

<style scoped>
.visa-page {
  min-height: 100vh;
  position: relative;
}

/* Editorial hero backdrop photo */
.hero-photo-visa {
  --hero-photo: url("~@/assets/images/past Ahrs/mass.jpg");
}

/* Hero apply button */
.ahrs-page-actions {
  margin-top: 26px;
  display: flex;
  justify-content: center;
}
.hero-apply-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: #fbf7ec;
  color: #0a3a1f;
  font-weight: 700;
  font-size: 1rem;
  padding: 13px 30px;
  border-radius: 50px;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}
.hero-apply-btn:hover {
  transform: translateY(-2px);
  background: #ffffff;
  color: #0a3a1f;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
}

.container-narrow {
  max-width: none;
  width: 100%;
  margin: 0 auto;
  padding: 0 clamp(20px, 4vw, 64px);
}

.visa-content {
  position: relative;
  z-index: 2;
  padding: 44px 0 64px;
}

/* Multi-column card grid */
.visa-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}
.span-2 {
  grid-column: 1 / -1;
}
.visa-grid .visa-card {
  margin-bottom: 0;
}
.visa-grid .visa-cta {
  margin-top: 0;
}
@media (max-width: 768px) {
  .visa-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.visa-card {
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(17, 149, 73, 0.16);
  border-radius: 16px;
  padding: 22px 26px;
  margin-bottom: 14px;
  box-shadow: 0 8px 26px rgba(17, 149, 73, 0.07);
}

.highlight-card {
  text-align: center;
  border-color: rgba(17, 149, 73, 0.32);
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%);
}

.checker-card {
  text-align: center;
  border-color: rgba(17, 149, 73, 0.4);
  box-shadow: 0 16px 44px rgba(17, 149, 73, 0.14);
}

.visa-card-icon {
  width: 50px;
  height: 50px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: var(--gradient-primary, linear-gradient(135deg, #1a472a, #2d5f3f));
  color: #4ade80;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
}

.visa-card-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.2rem, 2vw, 1.5rem);
  font-weight: 700;
  color: #0a3a1f;
  margin-bottom: 8px;
}

.visa-card-text {
  font-size: 0.96rem;
  line-height: 1.6;
  color: #2a3d33;
  margin-bottom: 12px;
}

.visa-card-note {
  font-size: 0.82rem;
  color: #6f8a79;
  margin: 12px 0 0;
}

/* Eligibility checker */
.elig-box {
  position: relative;
  max-width: 460px;
  margin: 0 auto;
}
.elig-field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px 4px 16px;
  background: #ffffff;
  border: 2px solid rgba(17, 149, 73, 0.3);
  border-radius: 50px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.elig-field:focus-within {
  border-color: #119549;
  box-shadow: 0 0 0 4px rgba(17, 149, 73, 0.12);
}
.elig-field > i {
  color: #119549;
  font-size: 0.95rem;
}
.elig-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: #0a3a1f;
  padding: 12px 4px;
}
.elig-clear {
  border: none;
  background: rgba(17, 149, 73, 0.1);
  color: #0c6e37;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;
}
.elig-clear:hover {
  background: rgba(17, 149, 73, 0.2);
}

/* Custom dropdown */
.elig-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 30;
  list-style: none;
  margin: 0;
  padding: 6px;
  background: #ffffff;
  border: 1px solid rgba(17, 149, 73, 0.2);
  border-radius: 14px;
  box-shadow: 0 18px 44px rgba(10, 58, 31, 0.18);
  max-height: 304px;
  overflow-y: auto;
  text-align: left;
}
.elig-option {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 14px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.95rem;
  color: #0a3a1f;
  transition: background 0.15s ease;
}
.elig-option:hover {
  background: rgba(17, 149, 73, 0.1);
}
.elig-option i {
  color: #119549;
  font-size: 0.82rem;
  flex-shrink: 0;
}
.elig-hl {
  background: rgba(0, 222, 0, 0.22);
  color: #06351c;
  border-radius: 3px;
  padding: 0 1px;
}
.elig-hint {
  font-size: 0.8rem;
  color: #6f8a79;
  margin: 8px 0 0;
}

/* Dropdown scrollbar */
.elig-dropdown::-webkit-scrollbar { width: 8px; }
.elig-dropdown::-webkit-scrollbar-thumb {
  background: rgba(17, 149, 73, 0.3);
  border-radius: 8px;
}

.dd-fade-enter-active,
.dd-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.dd-fade-enter-from,
.dd-fade-leave-to { opacity: 0; transform: translateY(-6px); }

.elig-result {
  text-align: left;
  margin: 14px auto 0;
  max-width: 560px;
  padding: 16px 20px;
  border-radius: 14px;
  border-left: 5px solid #119549;
  background: #f0fdf4;
}
.elig-result-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
}
.elig-result-head > i {
  font-size: 1.6rem;
  flex-shrink: 0;
}
.elig-country {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6f8a79;
  margin: 0;
}
.elig-verdict {
  font-size: 1.08rem;
  font-weight: 700;
  color: #0a3a1f;
  margin: 0;
}
.elig-detail {
  font-size: 0.94rem;
  line-height: 1.6;
  color: #2a3d33;
  margin: 0;
}
.elig-note {
  font-size: 0.86rem;
  line-height: 1.5;
  color: #425a4c;
  margin: 12px 0 0;
  padding-top: 12px;
  border-top: 1px dashed rgba(17, 149, 73, 0.25);
}
.elig-note i { margin-right: 5px; }

/* Tones */
.tone-free,
.tone-arrival {
  background: #f0fdf4;
  border-left-color: #16a34a;
}
.tone-free .elig-result-head > i,
.tone-arrival .elig-result-head > i { color: #16a34a; }

.tone-referral {
  background: #fef2f2;
  border-left-color: #dc2626;
}
.tone-referral .elig-result-head > i { color: #dc2626; }

.tone-online {
  background: #fffbeb;
  border-left-color: #d97706;
}
.tone-online .elig-result-head > i { color: #d97706; }

.elig-fade-enter-active,
.elig-fade-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.elig-fade-enter-from,
.elig-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* Lists */
.visa-list,
.visa-check {
  list-style: none;
  padding: 0;
  margin: 0;
}
.visa-list li {
  position: relative;
  padding-left: 22px;
  margin-bottom: 9px;
  font-size: 0.96rem;
  line-height: 1.55;
  color: #2a3d33;
}
.visa-list li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 9px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #119549;
}
.visa-check li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 9px;
  font-size: 0.96rem;
  line-height: 1.5;
  color: #2a3d33;
}
.visa-check li i {
  color: #119549;
  margin-top: 4px;
  flex-shrink: 0;
}

/* Visa table */
.visa-table-wrap { overflow-x: auto; }
.visa-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}
.visa-table th {
  text-align: left;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0c6e37;
  padding: 0 14px 12px;
  border-bottom: 2px solid rgba(17, 149, 73, 0.2);
}
.visa-table td {
  padding: 14px;
  border-bottom: 1px dashed rgba(17, 149, 73, 0.18);
  color: #2a3d33;
  vertical-align: top;
}
.visa-table td strong {
  display: block;
  color: #0a3a1f;
}
.visa-table td span {
  display: block;
  font-size: 0.82rem;
  color: #6f8a79;
  margin-top: 2px;
}
.visa-table .row-flag td {
  background: rgba(17, 149, 73, 0.06);
}
.visa-table .row-flag td:first-child {
  border-left: 3px solid #119549;
}

.visa-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--gradient-cta, linear-gradient(135deg, #4ade80, #22c55e));
  color: #06351c;
  font-weight: 700;
  padding: 13px 28px;
  border-radius: 50px;
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
}
.visa-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 34px rgba(34, 197, 94, 0.45);
  color: #06351c;
}

.visa-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0c6e37;
  font-weight: 600;
  text-decoration: none;
}
.visa-link:hover {
  text-decoration: underline;
}

/* CTA */
.visa-cta {
  text-align: center;
  margin-top: 22px;
  padding: 38px 32px;
  background: var(--gradient-primary, linear-gradient(135deg, #1a472a, #2d5f3f));
  border-radius: 20px;
  box-shadow: 0 16px 40px rgba(15, 36, 25, 0.28);
}
.cta-title {
  font-family: "Playfair Display", serif;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 10px;
}
.cta-subtitle {
  color: rgba(255, 255, 255, 0.85);
  max-width: 460px;
  margin: 0 auto 20px;
}
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: #06351c;
  font-weight: 700;
  padding: 13px 32px;
  border-radius: 50px;
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 30px rgba(74, 222, 128, 0.3);
}
.cta-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 40px rgba(74, 222, 128, 0.5);
  color: #06351c;
}

@media (max-width: 768px) {
  .visa-card { padding: 26px 22px; }
  .visa-cta { padding: 40px 24px; }
}
</style>
